import type {PayloadAction} from '@reduxjs/toolkit';
import {getNumParentAccounts} from '@Libraries/social-media-account.library';
import {createSlice} from '@reduxjs/toolkit';
import type {
  MediaTypeLoadingStates,
  MediaTypePublishCompletion,
  MediaTypePublishingParams,
  SocialMediaAccountId,
  SocialMediaAccountIdString,
  SocialPost,
} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {
  AccountNeedsResizeMap,
  AccountPublishableUrlForAllMediaTypeMap,
  AccountResizeStatusMap,
  AccountResizeStatusMapForMediaType,
  CaptionErrorByAccountIds,
  DesignPreviewUrl,
  DesignSelection,
  DesignsUrlMap,
  MultiGraphicDesignSelectorModeType,
  PublishableUrlUpdatePayload,
  PublishedUrlMapForAccount,
  PublishedUrlUpdatePayload,
  RetryStatusForMediaType,
  RetryStatusUpdatePayload,
  TitleErrorByAccountIds,
} from '@Components/social-media/social-media.types';
import {doesSocialPlatformHaveTitle, MultiGraphicDesignSelectorModes, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isAccountPlanLocked} from '@Components/social-media/account.vo';
import {getComparableSocialPlatform} from '@Libraries/social-media-platform.library';
import {socialMediaWizardExtraReducers} from '@Components/social-media-wizard/social-media-wizard-extra-reducer';
import {
  CaptionErrorTypes,
  countHashtags,
  type DesignsGraphicMetaUpdatePayload,
  type GraphicsReadinessState,
  socialAccountPostCoverValidations,
  SocialMediaWizardMediaTypeSelection,
  SocialMediaWizardStep,
  titleValidations,
} from '@Components/social-media-wizard/social-media-wizard.types';
import type {
  GraphicsMetaMapType,
  PublishingParamsBoardIdPayload,
  PublishingParamsReelMusicPayload,
  PublishingParamsCaptionPayload,
  PublishingParamsCommonCaptionPayload,
  PublishingParamsCommonPostCoverPayload,
  PublishingParamsCommonTextPayload,
  PublishingParamsDisableComment,
  PublishingParamsDisableDuet,
  PublishingParamsDisableStitch,
  PublishingParamsIsPromotingThirdParty,
  PublishingParamsIsSelfBrand,
  PublishingParamsLinkedInPrivacySettingsPayload,
  PublishingParamsPublishErrorMessagePayload,
  PublishingParamsSocialPostCoverPayload,
  PublishingParamsTextUpdatePayload,
  PublishingParamsTikTokPrivacySettingsPayload,
  PublishingParamsTitlePayload,
  PublishingParamsWebsiteLinkPayload,
  PublishingParamsYouTubePrivacySettingsPayload,
  PublishingStatusForAccountPayload,
  SocialMediaPublishingParams,
  SocialPostCover,
  SocialPostPublishingAction,
} from '@Components/social-media/publish-params.types';
import {RetryStatus} from '@Components/social-media/publish-params.types';
import {getAccountForId, getCharacterLimitForAccountPost, getEmptyPublishingParamsForAccount} from '@Libraries/social-media-wizard-accounts-library';
import {LoadingStates} from '@Utils/loading.util';
import type {AccountResizeStatusForDesignPayload} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import type {ErrorBoxInputs} from '@Components/error-box';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {getDefaultPublishParamsForMediaType, getNonStoryPlatformMediaType, getNonStoryPublishingParams, getStoryPublishingParams} from '@Libraries/social-media-params.library';
import {getStoryGuidelineforIndex, getUnusedRandomGuidelineNumber} from '@Components/social-media-wizard/social-media-wizard-library';
import type {StoryGuideline} from '@Components/social-media-wizard/components/social-media-publish-params-story-guideline';
import {isValidURL} from '@Utils/url.util';
import {omitKey} from '@Utils/record.util';
import cloneDeep from 'lodash/cloneDeep';

type TriggerCounter = number;

interface RetainedNonStoryText {
  caption: string;
  title: string;
}

export interface SocialMediaWizardState {
  post?: SocialPost;
  accounts: SocialAccount[];
  accountIdsToPublishTo: number[];
  commonCaption: string;
  hasCommonCaption: boolean;
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>;
  retainedNonStoryTextForAccount: Record<SocialMediaAccountId, RetainedNonStoryText>;
  accountTypeToConnect?: SocialPlatform;
  wizardStep: SocialMediaWizardStep;
  isTemplateSelected: boolean;
  accountLevelErrorMessage: ErrorBoxInputs;
  nextButtonDisabled: boolean;
  hideNextButton: boolean;
  savingPublishingParamsState: LoadingStates;
  publishingAction?: SocialPostPublishingAction;
  publishingStatusForMediaTypes: Record<SocialMediaAccountId, MediaTypeLoadingStates>;
  publishCompletedForMediaTypes: Record<SocialMediaAccountId, MediaTypePublishCompletion>;
  nextButtonText: string;
  nextButtonIcon: string;
  triggerNext: TriggerCounter;
  triggerPrevious: TriggerCounter;
  captionError: CaptionErrorByAccountIds;
  titleError: TitleErrorByAccountIds;
  areAccountsLoading: boolean;
  publishedAccountUrlMapForMediaType: PublishedUrlMapForAccount;
  numParentAccountsConnected: number;
  retryPublishingMapForAccount: Record<SocialMediaAccountId, RetryStatusForMediaType>;
  hasSeenOnboarding: boolean;
  hasSeenConfetti: boolean;
  hasUserPublishedBefore: boolean;
  isSocialMediaWizardAiCaptionsEnabled: boolean;
  isGoogleBusinessProfileEnabled: boolean;
  designsToPublish: DesignSelection[];
  designsGraphicsMetaMap: GraphicsMetaMapType;
  polledPreviewUrls: DesignPreviewUrl[];
  accountNeedsResizeMap: AccountNeedsResizeMap;
  multiGraphicDesignSelectorMode: MultiGraphicDesignSelectorModeType;
  designSelectorTab: DesignSelectorTab;
  accountResizeStatusMapForMediaType: AccountResizeStatusMapForMediaType;
  publishableGraphicUrlForAllMediaTypeMap: AccountPublishableUrlForAllMediaTypeMap;
  platformSelectionForAccount: Record<SocialMediaAccountId, SocialMediaWizardMediaTypeSelection>;
  designToResize: DesignSelection | undefined;
  storyGuidelineForAccount: Record<SocialMediaAccountId, StoryGuideline>;
  hasCommonPostCover: boolean;
  emphasizedPlatform?: SocialPlatform;
  areCaptionsBeingAutoGenerated: boolean;
  graphicsReadiness: GraphicsReadinessState;
}

const initialState: SocialMediaWizardState = {
  post: undefined,
  accounts: [],
  accountIdsToPublishTo: [],
  commonCaption: '',
  hasCommonCaption: true,
  publishingParams: {},
  retainedNonStoryTextForAccount: {},
  accountTypeToConnect: undefined,
  isTemplateSelected: false,
  wizardStep: SocialMediaWizardStep.SELECT_DESIGN,
  accountLevelErrorMessage: {
    title: '',
    description: '',
  },
  nextButtonDisabled: false,
  hideNextButton: false,
  savingPublishingParamsState: LoadingStates.NOT_LOADED,
  publishingAction: undefined,
  nextButtonText: window.i18next.t('pmwjs_next'),
  nextButtonIcon: 'icon-arrow-right',
  triggerNext: 0,
  triggerPrevious: 0,
  captionError: {},
  titleError: {},
  areAccountsLoading: true,
  publishedAccountUrlMapForMediaType: {},
  numParentAccountsConnected: 0,
  retryPublishingMapForAccount: {},
  hasSeenOnboarding: false,
  hasSeenConfetti: false,
  hasUserPublishedBefore: false,
  isSocialMediaWizardAiCaptionsEnabled: false,
  isGoogleBusinessProfileEnabled: false,
  designsToPublish: [],
  designsGraphicsMetaMap: {},
  polledPreviewUrls: [],
  accountNeedsResizeMap: {},
  multiGraphicDesignSelectorMode: MultiGraphicDesignSelectorModes.SELECT,
  designSelectorTab: DesignSelectorTab.MY_DESIGNS,
  accountResizeStatusMapForMediaType: {},
  publishableGraphicUrlForAllMediaTypeMap: {},
  publishingStatusForMediaTypes: {},
  publishCompletedForMediaTypes: {},
  platformSelectionForAccount: {},
  designToResize: undefined,
  storyGuidelineForAccount: {},
  hasCommonPostCover: true,
  emphasizedPlatform: undefined,
  areCaptionsBeingAutoGenerated: false,
  graphicsReadiness: {status: LoadingStates.NOT_LOADED},
};

export const socialMediaWizardSlice = createSlice({
  name: 'socialMediaWizard',
  initialState,
  reducers: {
    updateAccountTypeToConnect: (state, {payload}: PayloadAction<SocialPlatform>) => {
      doUpdateAccountTypeToConnect(state, payload);
    },
    updateAccountIdsToPublishTo: (state, {payload}: PayloadAction<number[]>) => {
      doUpdateAccountIdsToPublishTo(state, payload);
    },
    updateIsTemplateSelected: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateIsTemplateSelected(state, payload);
    },
    updateAccountLevelErrorMessage: (state, {payload}: PayloadAction<ErrorBoxInputs>) => {
      doUpdateAccountLevelErrorMessage(state, payload);
    },
    hideAccountLevelErrorMessage: (state) => {
      doUpdateAccountLevelErrorMessage(state, {title: '', description: ''});
    },
    updatePublishingParams: (state, {payload}: PayloadAction<Record<SocialMediaAccountIdString, MediaTypePublishingParams>>) => {
      doUpdatePublishingParams(state, payload);
    },
    updateCommonCaption: (state, {payload}: PayloadAction<string>) => {
      doUpdateCommonCaption(state, payload);
    },
    updatePublishingStatusForAllAccountMediaTypes: (state, {payload}: PayloadAction<LoadingStates>) => {
      doUpdatePublishingStatusForAllAccountMediaTypes(state, payload);
    },
    resetIncompletePublishingStatuses: (state) => {
      doResetIncompletePublishingStatuses(state);
    },
    updateHasSeenOnboarding: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateHasSeenOnboarding(state, payload);
    },
    updateHasSeenConfetti: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateHasSeenConfetti(state, payload);
    },
    updateHasUserPublishedBefore: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateHasUserPublishedBefore(state, payload);
    },
    updateIsSocialMediaWizardAiCaptionsEnabled: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateIsSocialMediaWizardAiCaptionsEnabled(state, payload);
    },
    updateIsGoogleBusinessProfileEnabled: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateIsGoogleBusinessProfileEnabled(state, payload);
    },
    updateHasCommonCaption: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateHasCommonCaption(state, payload);
    },
    updateEmphasizedPlatform: (state, {payload}: PayloadAction<SocialPlatform | undefined>) => {
      doUpdateEmphasizedPlatform(state, payload);
    },
    updateHasCommonPostCover: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateHasCommonPostCover(state, payload);
    },
    updateNextButtonText: (state, {payload}: PayloadAction<string>) => {
      doUpdateNextButtonText(state, payload);
    },
    updateNumParentAccountsConnected: (state, {payload}: PayloadAction<number>) => {
      doUpdateNumParentAccountsConnected(state, payload);
    },
    syncPublishingParams: (state) => {
      doSyncPublishingParams(state);
    },
    updateNextButtonIcon: (state, {payload}: PayloadAction<string>) => {
      doUpdateNextButtonIcon(state, payload);
    },
    updatePost: (state, {payload}: PayloadAction<SocialPost>) => {
      doUpdatePost(state, payload);
    },
    updateWizardStep: (state, {payload}: PayloadAction<SocialMediaWizardStep>) => {
      doUpdateWizardStep(state, payload);
    },
    updateTriggerNext: (state) => {
      doUpdateTriggerNext(state);
    },
    updatePublishingStatusForAccountWithMediaType: (state, {payload}: PayloadAction<PublishingStatusForAccountPayload>) => {
      doUpdatePublishingStatusForAccountWithMediaType(state, payload);
    },
    updatePublishingParamCaption: (state, {payload}: PayloadAction<PublishingParamsCaptionPayload>) => {
      doUpdatePublishingParamCaption(state, payload);
    },
    updateSavingPublishingParamsState: (state, {payload}: PayloadAction<LoadingStates>) => {
      doUpdateSavingPublishingParamsState(state, payload);
    },
    updatePublishingParamTitle: (state, {payload}: PayloadAction<PublishingParamsTitlePayload>) => {
      doUpdatePublishingParamTitle(state, payload);
    },
    updatePublishingParamDisableComment: (state, {payload}: PayloadAction<PublishingParamsDisableComment>) => {
      doUpdatePublishingParamDisableComment(state, payload);
    },
    updatePublishingParamDisableStitch: (state, {payload}: PayloadAction<PublishingParamsDisableStitch>) => {
      doUpdatePublishingParamDisableStitch(state, payload);
    },
    updatePublishingParamDisableDuet: (state, {payload}: PayloadAction<PublishingParamsDisableDuet>) => {
      doUpdatePublishingParamDisableDuet(state, payload);
    },
    updatePublishingParamIsSelfBrand: (state, {payload}: PayloadAction<PublishingParamsIsSelfBrand>) => {
      doUpdatePublishingParamIsSelfBrand(state, payload);
    },
    updatePublishingParamIsPromotingThirdParty: (state, {payload}: PayloadAction<PublishingParamsIsPromotingThirdParty>) => {
      doUpdatePublishingParamIsPromotingThirdParty(state, payload);
    },
    updateTikTokPublishingParamPrivacySettings: (state, {payload}: PayloadAction<PublishingParamsTikTokPrivacySettingsPayload>) => {
      doUpdateTikTokPublishingParamPrivacySettings(state, payload);
    },
    updateLinkedInPublishingParamPrivacySettings: (state, {payload}: PayloadAction<PublishingParamsLinkedInPrivacySettingsPayload>) => {
      doUpdateLinkedInPublishingParamPrivacySettings(state, payload);
    },
    updateYouTubePublishingParamPrivacySettings: (state, {payload}: PayloadAction<PublishingParamsYouTubePrivacySettingsPayload>) => {
      doUpdateYouTubePublishingParamPrivacySettings(state, payload);
    },
    updatePublishingParamAllCaptions: (state, {payload}: PayloadAction<PublishingParamsCommonCaptionPayload>) => {
      doUpdatePublishingParamAllCaptions(state, payload);
    },
    updatePublishingParamAllText: (state, {payload}: PayloadAction<PublishingParamsCommonTextPayload>) => {
      doUpdatePublishingParamAllText(state, payload);
    },
    updatePublishingParamSocialPostCover: (state, {payload}: PayloadAction<PublishingParamsSocialPostCoverPayload>) => {
      doUpdatePublishingParamSocialPostCover(state, payload);
    },
    resetSocialPostCoverForAllPublishingParams: (state) => {
      doResetSocialPostCoverForAllPublishingParams(state);
    },
    updatePublishingAction: (state, {payload}: PayloadAction<SocialPostPublishingAction | undefined>) => {
      doUpdatePublishingAction(state, payload);
    },
    updateWizardSocialAccount: (state, {payload}: PayloadAction<SocialAccount>) => {
      doUpdateWizardSocialAccount(state, payload);
    },
    removeWizardSocialAccount: (state, {payload}: PayloadAction<SocialMediaAccountId>) => {
      doRemoveWizardSocialAccount(state, payload);
    },
    disableNextButton: (state) => {
      doDisableNextButton(state);
    },
    enableNextButton: (state) => {
      doEnableNextButton(state);
    },
    hideNextButton: (state) => {
      doHideNextButton(state);
    },
    showNextButton: (state) => {
      doShowNextButton(state);
    },
    updateTriggerPrevious: (state) => {
      doUpdateTriggerPrevious(state);
    },
    initPublishingParams: (state) => {
      const userAccountIds = state.accountIdsToPublishTo;

      userAccountIds.forEach((idSocialMediaAccount) => {
        setEmptyStatePublishingParamsIfNeeded(state, idSocialMediaAccount);
      });

      initTitleErrors(state);
      initCaptionErrors(state);
      initGraphicsMeta(state);
      initPublishableUrls(state);
      doInitializePublishUrlMapForAccounts(state);
      doInitializePublishStatusForAllAccounts(state);
      doInitializeRetryPublishingMapForAccounts(state);
      doInitializePlatformMediaTypeMap(state);
      initStoryGuidelines(state);
    },
    removeAccountIdFromPublishing: (state, {payload}: PayloadAction<number>) => {
      doRemoveAccountIdFromPublishing(state, payload);
    },
    removeAccountsFromWizard: (state, {payload}: PayloadAction<number[]>) => {
      doRemoveAccountsFromWizard(state, payload);
    },
    removeAccountFromPublishingParams: (state, {payload}: PayloadAction<number>) => {
      doRemoveAccountFromPublishingParams(state, payload);
    },
    addAccountIdForPublishing: (state, {payload}: PayloadAction<number>) => {
      doAddAccountIdForPublishing(state, payload);
    },
    setAccountResizeStatusForMediaType: (state, {payload}: PayloadAction<AccountResizeStatusForDesignPayload>) => {
      doSetAccountResizeStatusForMediaType(state, payload);
    },
    resetAccountResizeStatusForDesignsMap: (state): void => {
      doResetAccountResizeStatusForMediaType(state);
    },
    initRetryPublishingMapForAccount: (state): void => {
      doInitializeRetryPublishingMapForAccounts(state);
    },
    initPublishingStatusMapForAccounts: (state): void => {
      doInitializePublishStatusForAllAccounts(state);
    },
    initPublishedUrlMapForAccounts: (state): void => {
      doInitializePublishUrlMapForAccounts(state);
    },
    setRetryingStatusForAccountWithMediaType: (state, {payload}: PayloadAction<RetryStatusUpdatePayload>) => {
      doSetRetryingStatusForAccountWithMediaType(state, payload.accountId, payload.platformMediaType);
    },
    resetStateToDraft: (state) => {
      doResetStateToDraft(state);
    },
    updateAreAccountsLoading: (state, {payload}: PayloadAction<boolean>) => {
      doUpdateAreAccountsLoading(state, payload);
    },
    unsetRetryingStatusForAccountWithMediaType: (state, {payload}: PayloadAction<RetryStatusUpdatePayload>) => {
      doUnsetRetryingStatusForAccountWithMediaType(state, payload.accountId, payload.platformMediaType);
    },
    updatePublishErrorMessageForAccount: (state, {payload}: PayloadAction<PublishingParamsPublishErrorMessagePayload>) => {
      doUpdatePublishErrorMessageForAccount(state, payload);
    },
    updatePublishErrorMessageForAllParams: (state, {payload}: PayloadAction<string>) => {
      doUpdatePublishErrorMessageForAllParams(state, payload);
    },
    updateDesignsToPublish: (state, {payload}: PayloadAction<DesignSelection[]>) => {
      doUpdateDesignsToPublish(state, payload);
    },
    updateDesignsToPublishAndTriggerNext: (state, {payload}: PayloadAction<DesignSelection[]>) => {
      doUpdateDesignsToPublish(state, payload);
      doUpdateTriggerNext(state);
    },
    clearPolledPreviewUrls: (state) => {
      state.polledPreviewUrls = [];
    },
    updatePublishedAccountPostUrlMapForAccountWithMediaType: (state, {payload}: PayloadAction<PublishedUrlUpdatePayload>) => {
      doUpdatePublishedAccountUrlForMediaType(state, payload);
    },
    updateMultiGraphicDesignSelectorMode: (state, {payload}: PayloadAction<MultiGraphicDesignSelectorModeType>) => {
      state.multiGraphicDesignSelectorMode = payload;
    },
    toggleToPostOnly: (state, {payload}: PayloadAction<number>) => {
      const socialPostCover = getPosterCoverCopyFromPublishParams(state, state.publishingParams[payload].REELS);

      retainNonStoryText(state, payload);
      state.publishingParams[payload].STORIES = null;
      state.publishingParams[payload].REELS = null;
      doSetPublishToPost(state, payload, socialPostCover);
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    toggleToReelOnly: (state, {payload}: PayloadAction<number>) => {
      const socialPostCover = getPosterCoverCopyFromPublishParams(state, state.publishingParams[payload].POST);

      retainNonStoryText(state, payload);
      state.publishingParams[payload].STORIES = null;
      state.publishingParams[payload].POST = null;
      doSetPublishToReel(state, payload, socialPostCover);
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    toggleToStoryOnly: (state, {payload}: PayloadAction<number>) => {
      retainNonStoryText(state, payload);
      state.publishingParams[payload].POST = null;
      state.publishingParams[payload].REELS = null;
      doSetPublishToStory(state, payload);
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    setPublishToPost: (state, {payload}: PayloadAction<number>) => {
      retainNonStoryText(state, payload);
      doSetPublishToPost(state, payload);
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    setPublishToReel: (state, {payload}: PayloadAction<number>) => {
      retainNonStoryText(state, payload);
      doSetPublishToReel(state, payload);
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    setPublishToStory: (state, {payload}: PayloadAction<number>) => {
      doSetPublishToStory(state, payload);
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    unsetPublishToStory: (state, {payload}: PayloadAction<number>) => {
      state.publishingParams[payload].STORIES = null;
      doSyncPublishingStatusesWithParamsForAccount(state, payload);
    },
    updatePinterestPublishingParamsBoardId: (state, {payload}: PayloadAction<PublishingParamsBoardIdPayload>) => {
      doUpdatePinterestBoardIdForAccount(state, payload);
    },
    updateInstagramReelMusic: (state, {payload}: PayloadAction<PublishingParamsReelMusicPayload>) => {
      doUpdateInstagramReelMusicForAccount(state, payload);
    },
    updatePinterestPublishingParamsWebsiteLink: (state, {payload}: PayloadAction<PublishingParamsWebsiteLinkPayload>) => {
      doUpdatePinterestWebsiteLinkForAccount(state, payload);
    },
    syncEditedDesign: (state, {payload}: PayloadAction<DesignSelection>): void => {
      doSyncEditedDesign(state, payload);
    },
    updateDesignSelectorTab: (state: SocialMediaWizardState, {payload}: PayloadAction<DesignSelectorTab>): void => {
      state.designSelectorTab = payload;
    },
    updateGraphicURLInGraphicsMetaForDesign: (state: SocialMediaWizardState, {payload}: PayloadAction<DesignsGraphicMetaUpdatePayload>) => {
      doUpdateGraphicURLForGraphicsMetaMap(state, payload);
    },
    updatePublishableUrl: (state: SocialMediaWizardState, {payload}: PayloadAction<PublishableUrlUpdatePayload>) => {
      doUpdatePublishableUrl(state, payload);
    },
    setMediaTypeSelectionToPost: (state: SocialMediaWizardState, {payload}: PayloadAction<number>) => {
      state.platformSelectionForAccount[payload] = SocialMediaWizardMediaTypeSelection.POST;
    },
    setMediaTypeSelectionToStory: (state: SocialMediaWizardState, {payload}: PayloadAction<number>) => {
      state.platformSelectionForAccount[payload] = SocialMediaWizardMediaTypeSelection.STORY;
    },
    setMediaTypeSelectionToPostAndStory: (state: SocialMediaWizardState, {payload}: PayloadAction<number>) => {
      state.platformSelectionForAccount[payload] = SocialMediaWizardMediaTypeSelection.POST_AND_STORY;
    },
    setMediaTypeSelectionToReel: (state: SocialMediaWizardState, {payload}: PayloadAction<number>) => {
      state.platformSelectionForAccount[payload] = SocialMediaWizardMediaTypeSelection.REEL;
    },
    setMediaTypeSelectionToReelAndStory: (state: SocialMediaWizardState, {payload}: PayloadAction<number>) => {
      state.platformSelectionForAccount[payload] = SocialMediaWizardMediaTypeSelection.REEL_AND_STORY;
    },
    updateDesignToResize: (state, {payload}: PayloadAction<DesignSelection | undefined>) => {
      state.designToResize = payload;
    },
    setAreCaptionsBeingAutoGenerated: (state, {payload}: PayloadAction<boolean>) => {
      state.areCaptionsBeingAutoGenerated = payload;
    },
    updateGraphicsReadiness: (state, {payload}: PayloadAction<LoadingStates>) => {
      state.graphicsReadiness.status = payload;
    },
    updateAccountNeedsResizeMap: (state, {payload}: PayloadAction<AccountNeedsResizeMap>) => {
      state.accountNeedsResizeMap = payload;
    },
    updateDesignsGraphicMetaMap: (state, {payload}: PayloadAction<GraphicsMetaMapType>) => {
      state.designsGraphicsMetaMap = payload;
      applyPolledPreviewUrls(state);

      initGraphicsMeta(state);
    },
    updateDesignsGraphicURLs: (state, {payload}: PayloadAction<DesignPreviewUrl[]>) => {
      state.polledPreviewUrls = payload;
      applyPolledPreviewUrls(state);

      initGraphicsMeta(state);
    },
  },
  extraReducers: socialMediaWizardExtraReducers,
});

const getDesignSelectionKey = (design: DesignSelection): string => {
  return `${design.posterHashedId}:${design.pageHashedId}`;
};

const isDifferentDesignSelection = (currentDesigns: DesignSelection[], nextDesigns: DesignSelection[]): boolean => {
  if (currentDesigns.length === 0 || nextDesigns.length === 0) {
    return false;
  }

  const currentDesignKeys = new Set(currentDesigns.map(getDesignSelectionKey));

  return nextDesigns.length !== currentDesigns.length || nextDesigns.some((design) => !currentDesignKeys.has(getDesignSelectionKey(design)));
};

const clearNonStoryTextForAllAccounts = (state: SocialMediaWizardState): void => {
  state.commonCaption = '';
  state.retainedNonStoryTextForAccount = {};

  Object.values(state.publishingParams).forEach((paramsByMediaType) => {
    const nonStoryParams = getNonStoryPublishingParams(paramsByMediaType);

    if (!nonStoryParams) {
      return;
    }

    nonStoryParams.caption = '';

    setTitleIfSupported(nonStoryParams, '');
  });

  state.accountIdsToPublishTo.forEach((accountId) => {
    const account = getAccountForId(state.accounts, accountId);

    if (account) {
      doResetCaptionErrorForAccount(account, state);
      doResetTitleErrorForAccount(account, state);
    }
  });
};

const isStoryPublishingAvailableForDesigns = (designs: DesignSelection[]): boolean => {
  return designs.length <= 1;
};

const dropStoryParamsForAllAccounts = (state: SocialMediaWizardState): void => {
  Object.entries(state.publishingParams).forEach(([accountId, paramsByMediaType]) => {
    if (!paramsByMediaType[PlatformMediaType.STORIES]) {
      return;
    }

    if (getNonStoryPublishingParams(paramsByMediaType)) {
      paramsByMediaType[PlatformMediaType.STORIES] = null;
      return;
    }

    state.publishingParams = omitKey(state.publishingParams, accountId);
  });
};

const doUpdateDesignsToPublish = (state: SocialMediaWizardState, designs: DesignSelection[]): void => {
  if (isDifferentDesignSelection(state.designsToPublish, designs)) {
    clearNonStoryTextForAllAccounts(state);
  }

  if (!isStoryPublishingAvailableForDesigns(designs)) {
    dropStoryParamsForAllAccounts(state);
  }

  state.designsToPublish = designs;
  state.polledPreviewUrls = [];
};

const retainNonStoryText = (state: SocialMediaWizardState, accountId: number): void => {
  const nonStoryParams = getNonStoryPublishingParams(state.publishingParams[accountId]);

  if (nonStoryParams) {
    state.retainedNonStoryTextForAccount[accountId] = {
      caption: nonStoryParams.caption,
      title: getTitleOrEmpty(nonStoryParams),
    };
  }
};

const getCaptionForRebuiltNonStoryParams = (state: SocialMediaWizardState, accountId: number): string => {
  if (state.hasCommonCaption) {
    return state.commonCaption;
  }

  return state.retainedNonStoryTextForAccount[accountId]?.caption ?? '';
};

const getTitleForRebuiltNonStoryParams = (state: SocialMediaWizardState, accountId: number): string => {
  return state.retainedNonStoryTextForAccount[accountId]?.title ?? '';
};

const getTitleOrEmpty = (params: SocialMediaPublishingParams): string => {
  return 'title' in params ? params.title : '';
};

const setTitleIfSupported = (params: SocialMediaPublishingParams, title: string): void => {
  if ('title' in params) {
    params.title = title;
  }
};

const getNewPublishingParams = (
  state: SocialMediaWizardState,
  accountId: number,
  socialPostCover: SocialPostCover | undefined,
  platformMediaType: PlatformMediaType
): SocialMediaPublishingParams | null => {
  const matchedAccount = getAccountForId(state.accounts, accountId);

  if (!matchedAccount) {
    console.error('No matched account found');
    return null;
  }

  const publishingParams = getEmptyPublishingParamsForAccount(matchedAccount);

  if (!publishingParams) {
    return null;
  }

  if (platformMediaType !== PlatformMediaType.STORIES) {
    publishingParams.caption = getCaptionForRebuiltNonStoryParams(state, accountId);
    setTitleIfSupported(publishingParams, getTitleForRebuiltNonStoryParams(state, accountId));
  }

  const newGraphicsMetaMap: GraphicsMetaMapType = {};
  const posters = state.designsToPublish;

  for (const poster of posters) {
    const {posterHashedId, pageHashedId} = poster;
    newGraphicsMetaMap[posterHashedId] ??= {};

    newGraphicsMetaMap[posterHashedId][pageHashedId] = {
      isVideo: state.designsGraphicsMetaMap[posterHashedId][pageHashedId].isVideo,
      graphicURL: state.publishableGraphicUrlForAllMediaTypeMap[accountId][platformMediaType][posterHashedId][pageHashedId],
      name: state.designsGraphicsMetaMap[posterHashedId][pageHashedId].name,
      order: state.designsGraphicsMetaMap[posterHashedId][pageHashedId].order,
    };
  }

  publishingParams.graphicsMetaMap = newGraphicsMetaMap;

  if (platformMediaType !== PlatformMediaType.STORIES && isSingleGraphicPost(state)) {
    setPostCoverInPublishParamsIfNeeded(socialPostCover, publishingParams, state.designsToPublish[0]);
  }

  return publishingParams;
};

const doSetPublishToPost = (state: SocialMediaWizardState, accountId: number, socialPostCover?: SocialPostCover): void => {
  const newPublishingParams = getNewPublishingParams(state, accountId, socialPostCover, PlatformMediaType.POST);

  if (!newPublishingParams) {
    return;
  }

  state.publishingParams[accountId] = {
    ...state.publishingParams[accountId],
    [PlatformMediaType.POST]: newPublishingParams,
    [PlatformMediaType.REELS]: null,
  };
};

const doSetPublishToReel = (state: SocialMediaWizardState, accountId: number, socialPostCover?: SocialPostCover): void => {
  const newPublishingParams = getNewPublishingParams(state, accountId, socialPostCover, PlatformMediaType.REELS);

  if (!newPublishingParams) {
    return;
  }

  state.publishingParams[accountId] = {
    ...state.publishingParams[accountId],
    [PlatformMediaType.REELS]: newPublishingParams,
    [PlatformMediaType.POST]: null,
  };
};

const doSetPublishToStory = (state: SocialMediaWizardState, accountId: number): void => {
  const newPublishingParms = getNewPublishingParams(state, accountId, undefined, PlatformMediaType.STORIES);

  if (!newPublishingParms) {
    return;
  }

  state.publishingParams[accountId] = {
    ...state.publishingParams[accountId],
    [PlatformMediaType.STORIES]: newPublishingParms,
  };
};

const doUpdatePublishableUrl = (state: SocialMediaWizardState, payload: PublishableUrlUpdatePayload): void => {
  const {accountId} = payload;
  const {posterHashId} = payload;
  const {pageHashId} = payload;
  const {platformMediaType} = payload;
  const {graphicUrl} = payload;

  state.publishableGraphicUrlForAllMediaTypeMap[accountId][platformMediaType][posterHashId][pageHashId] = graphicUrl;
};

const doSyncEditedDesign = (state: SocialMediaWizardState, designEdited: DesignSelection): void => {
  const oldDesign = state.designsToPublish[0];

  if (!oldDesign) {
    console.error('No design ID found');
    return;
  }

  replacePosterInDesigns(state, designEdited);
  replacePosterInGraphicsMetaMap(state, oldDesign, designEdited);
  doResetAccountResizeStatusForMediaType(state);
};

const replacePosterInGraphicsMetaMap = (state: SocialMediaWizardState, oldDesign: DesignSelection, newDesign: DesignSelection): void => {
  const graphicsMeta = state.designsGraphicsMetaMap[oldDesign.posterHashedId]?.[oldDesign.pageHashedId];

  if (!graphicsMeta) {
    console.error('Graphic meta not found for design ', oldDesign);
    return;
  }

  state.designsGraphicsMetaMap = {
    [newDesign.posterHashedId]: {
      [newDesign.pageHashedId]: graphicsMeta,
    },
  };
};

const replacePosterInDesigns = (state: SocialMediaWizardState, newDesign: DesignSelection): void => {
  state.designsToPublish = [newDesign];
};

const doUpdateGraphicURLForGraphicsMetaMap = (state: SocialMediaWizardState, payload: DesignsGraphicMetaUpdatePayload): void => {
  const {idSocialMediaAccount} = payload;
  const {mediaType} = payload;
  const {designHashId} = payload;
  const {pageHashId} = payload;

  const publishingParamsForMediaType = state.publishingParams[idSocialMediaAccount]?.[mediaType];

  if (!publishingParamsForMediaType) {
    console.error('No publishing params for Media type found');
    return;
  }

  const pageGraphicsMeta = publishingParamsForMediaType.graphicsMetaMap[designHashId]?.[pageHashId];

  if (!pageGraphicsMeta) {
    console.error('No graphics meta entry found for design page');
    return;
  }

  pageGraphicsMeta.graphicURL = payload.previewURL;
};

const initPublishableUrls = (state: SocialMediaWizardState): void => {
  const connectedAccounts = state.accounts;
  const publishableUrlsForMediaType: AccountPublishableUrlForAllMediaTypeMap = {};
  const designsToPublish = state.designsToPublish;
  const mapForDesigns: DesignsUrlMap = {};

  for (const design of designsToPublish) {
    mapForDesigns[design.posterHashedId] ??= {};
    mapForDesigns[design.posterHashedId][design.pageHashedId] = state.designsGraphicsMetaMap[design.posterHashedId][design.pageHashedId].graphicURL;
  }

  for (const account of connectedAccounts) {
    publishableUrlsForMediaType[account.id] = {
      [PlatformMediaType.POST]: mapForDesigns,
      [PlatformMediaType.STORIES]: mapForDesigns,
      [PlatformMediaType.REELS]: mapForDesigns,
    };
  }

  state.publishableGraphicUrlForAllMediaTypeMap = publishableUrlsForMediaType;
};

const doUpdateSavingPublishingParamsState = (state: SocialMediaWizardState, payload: LoadingStates): void => {
  state.savingPublishingParamsState = payload;
};

const doInitializePublishUrlMapForAccounts = (state: SocialMediaWizardState): void => {
  const accountsToPublishTo = state.accountIdsToPublishTo;

  for (const accountId of accountsToPublishTo) {
    const publishParams = state.publishingParams[accountId];

    if (!publishParams) {
      continue;
    }

    const storyParams = publishParams[PlatformMediaType.STORIES];
    const reelParams = publishParams[PlatformMediaType.REELS];
    const postParams = publishParams[PlatformMediaType.POST];

    state.publishedAccountUrlMapForMediaType[accountId] = {
      [PlatformMediaType.POST]: postParams ? '' : null,
      [PlatformMediaType.REELS]: reelParams ? '' : null,
      [PlatformMediaType.STORIES]: storyParams ? '' : null,
    };
  }
};

/**
 * Preserves an already-completed publish (LOADED) across a PublishingPost remount, but ONLY when backed by
 * completion evidence — because LOADED also encodes "no params for this media type" (see
 * {@link doSyncPublishingStatusesWithParamsForAccount} for that wedge). An evidence-less LOADED re-arms to
 * LOADING when params exist, so a slot whose params were (re)created can never skip its publish kickoff.
 */
const getInitialPublishStatusForMediaType = (existingStatus: LoadingStates | undefined, hasParams: boolean, hasCompletedPublish: boolean): LoadingStates => {
  if (existingStatus === LoadingStates.LOADED && hasCompletedPublish) {
    return LoadingStates.LOADED;
  }

  return hasParams ? LoadingStates.LOADING : LoadingStates.LOADED;
};

const doInitializePublishStatusForAllAccounts = (state: SocialMediaWizardState): void => {
  const accountsToPublishTo = state.accountIdsToPublishTo;

  for (const accountId of accountsToPublishTo) {
    const publishParams = state.publishingParams[accountId];

    if (!publishParams) {
      continue;
    }

    const storyParams = publishParams[PlatformMediaType.STORIES];
    const reelParams = publishParams[PlatformMediaType.REELS];
    const postParams = publishParams[PlatformMediaType.POST];

    const existingStatus = state.publishingStatusForMediaTypes[accountId];
    const completion = state.publishCompletedForMediaTypes[accountId];
    state.publishingStatusForMediaTypes[accountId] = {
      [PlatformMediaType.POST]: getInitialPublishStatusForMediaType(existingStatus?.[PlatformMediaType.POST], Boolean(postParams), Boolean(completion?.[PlatformMediaType.POST])),
      [PlatformMediaType.REELS]: getInitialPublishStatusForMediaType(
        existingStatus?.[PlatformMediaType.REELS],
        Boolean(reelParams),
        Boolean(completion?.[PlatformMediaType.REELS])
      ),
      [PlatformMediaType.STORIES]: getInitialPublishStatusForMediaType(
        existingStatus?.[PlatformMediaType.STORIES],
        Boolean(storyParams),
        Boolean(completion?.[PlatformMediaType.STORIES])
      ),
    };
  }
};

/**
 * Recomputes an account's per-media-type publishing statuses from its CURRENT params after a media-type
 * toggle creates or removes params. Must NOT go through getInitialPublishStatusForMediaType: a media type
 * without params is initialised to LOADED ("nothing to publish"), and that function preserves LOADED across
 * re-inits — so a slot whose params are created later (e.g. Story toggled on mid-session) would keep the
 * stale LOADED, skip its publish kickoff entirely, and falsely render as published while the backend post
 * wedges in PUBLISHING. Toggles only fire on the detail step, never while the publish screen is up, so no
 * genuinely-completed publish status can be clobbered here.
 */
const doSyncPublishingStatusesWithParamsForAccount = (state: SocialMediaWizardState, accountId: number): void => {
  const publishParams = state.publishingParams[accountId];

  if (!publishParams) {
    return;
  }

  state.publishingStatusForMediaTypes[accountId] = {
    [PlatformMediaType.POST]: publishParams[PlatformMediaType.POST] ? LoadingStates.LOADING : LoadingStates.LOADED,
    [PlatformMediaType.REELS]: publishParams[PlatformMediaType.REELS] ? LoadingStates.LOADING : LoadingStates.LOADED,
    [PlatformMediaType.STORIES]: publishParams[PlatformMediaType.STORIES] ? LoadingStates.LOADING : LoadingStates.LOADED,
  };
};

const doInitializePlatformMediaTypeMap = (state: SocialMediaWizardState): void => {
  const accountsToPublishTo = state.accountIdsToPublishTo;

  for (const accountId of accountsToPublishTo) {
    const storyExists = state.publishingParams[accountId].STORIES;
    const postExists = state.publishingParams[accountId].POST;
    const reelExists = state.publishingParams[accountId].REELS;
    let defaultType = SocialMediaWizardMediaTypeSelection.POST;

    if (storyExists) {
      if (reelExists) {
        defaultType = SocialMediaWizardMediaTypeSelection.REEL_AND_STORY;
      } else if (postExists) {
        defaultType = SocialMediaWizardMediaTypeSelection.POST_AND_STORY;
      } else {
        defaultType = SocialMediaWizardMediaTypeSelection.STORY;
      }
    } else if (reelExists) {
      defaultType = SocialMediaWizardMediaTypeSelection.REEL;
    }

    state.platformSelectionForAccount[accountId] = defaultType;
  }
};

const doInitializeRetryPublishingMapForAccounts = (state: SocialMediaWizardState): void => {
  const accountsToPublishTo = state.accountIdsToPublishTo;

  for (const accountId of accountsToPublishTo) {
    const publishParams = state.publishingParams[accountId];
    const storyParams = publishParams[PlatformMediaType.STORIES];
    const reelParams = publishParams[PlatformMediaType.REELS];
    const postParams = publishParams[PlatformMediaType.POST];

    state.retryPublishingMapForAccount[accountId] = {
      [PlatformMediaType.POST]: postParams ? RetryStatus.NOT_RETRYING : null,
      [PlatformMediaType.REELS]: reelParams ? RetryStatus.NOT_RETRYING : null,
      [PlatformMediaType.STORIES]: storyParams ? RetryStatus.NOT_RETRYING : null,
    };
  }
};

const doUpdateHasSeenOnboarding = (state: SocialMediaWizardState, payload: boolean): void => {
  state.hasSeenOnboarding = payload;
};

const doUpdateHasSeenConfetti = (state: SocialMediaWizardState, payload: boolean): void => {
  state.hasSeenConfetti = payload;
};

const doUpdateHasUserPublishedBefore = (state: SocialMediaWizardState, payload: boolean): void => {
  state.hasUserPublishedBefore = payload;
};

const doUpdateIsSocialMediaWizardAiCaptionsEnabled = (state: SocialMediaWizardState, payload: boolean): void => {
  state.isSocialMediaWizardAiCaptionsEnabled = payload;
};

const doUpdateIsGoogleBusinessProfileEnabled = (state: SocialMediaWizardState, payload: boolean): void => {
  state.isGoogleBusinessProfileEnabled = payload;
};

const doSetRetryingStatusForAccountWithMediaType = (state: SocialMediaWizardState, socialMediaAccountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  state.retryPublishingMapForAccount[socialMediaAccountId][platformMediaType] = RetryStatus.RETRYING;
};

const doResetStateToDraft = (state: SocialMediaWizardState): void => {
  doUpdatePublishingAction(state, undefined);
  doUpdatePublishingStatusForAllAccountMediaTypes(state, LoadingStates.LOADING);
  doUpdateSavingPublishingParamsState(state, LoadingStates.LOADING);
  doInitializePublishUrlMapForAccounts(state);
  state.publishCompletedForMediaTypes = {};
};

const doUnsetRetryingStatusForAccountWithMediaType = (state: SocialMediaWizardState, socialMediaAccountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
  state.retryPublishingMapForAccount[socialMediaAccountId][platformMediaType] = RetryStatus.NOT_RETRYING;
};

const doUpdatePublishingParamsProperty = (
  state: SocialMediaWizardState,
  idSocialMediaAccount: SocialMediaAccountId,
  paramsUpdateFunc: (publishingParams: SocialMediaPublishingParams) => void,
  platformMediaType: PlatformMediaType
): void => {
  const idSocialMediaAccountStr = idSocialMediaAccount;
  const matchedAccount = getAccountForId(state.accounts, idSocialMediaAccount);

  if (matchedAccount && typeof state.publishingParams[idSocialMediaAccountStr] !== 'undefined') {
    const oldPublishingParams: SocialMediaPublishingParams | null = state.publishingParams[idSocialMediaAccountStr][platformMediaType];

    if (!oldPublishingParams) {
      return;
    }

    paramsUpdateFunc(oldPublishingParams);

    state.publishingParams[idSocialMediaAccountStr][platformMediaType] = oldPublishingParams;
  }
};

const doUpdatePublishingStatusForAllAccountMediaTypes = (state: SocialMediaWizardState, payload: LoadingStates): void => {
  const accountIds = state.accountIdsToPublishTo;
  const updatedPublishingStatuses: Record<SocialMediaAccountId, MediaTypeLoadingStates> = {};

  accountIds.forEach((accountId) => {
    updatedPublishingStatuses[accountId] = {
      [PlatformMediaType.POST]: payload,
      [PlatformMediaType.REELS]: payload,
      [PlatformMediaType.STORIES]: payload,
    };

    // Bulk LOADED = completed save/schedule round; record evidence so the status survives re-inits.
    if (payload === LoadingStates.LOADED) {
      state.publishCompletedForMediaTypes[accountId] = {
        [PlatformMediaType.POST]: true,
        [PlatformMediaType.REELS]: true,
        [PlatformMediaType.STORIES]: true,
      };
    }
  });

  state.publishingStatusForMediaTypes = updatedPublishingStatuses;
};

const getSettledPublishingStatusesForAccount = (state: SocialMediaWizardState, accountId: SocialMediaAccountId): MediaTypeLoadingStates => {
  const completion = state.publishCompletedForMediaTypes[accountId];
  const existingStatus = state.publishingStatusForMediaTypes[accountId];

  const getStatusForMediaType = (mediaType: PlatformMediaType): LoadingStates => {
    const hasPublished = Boolean(completion?.[mediaType]) && existingStatus?.[mediaType] === LoadingStates.LOADED;
    return hasPublished ? LoadingStates.LOADED : LoadingStates.NOT_LOADED;
  };

  return {
    [PlatformMediaType.POST]: getStatusForMediaType(PlatformMediaType.POST),
    [PlatformMediaType.REELS]: getStatusForMediaType(PlatformMediaType.REELS),
    [PlatformMediaType.STORIES]: getStatusForMediaType(PlatformMediaType.STORIES),
  };
};

/** A completed slot keeps LOADED; without it getInitialPublishStatusForMediaType would republish. */
const doResetIncompletePublishingStatuses = (state: SocialMediaWizardState): void => {
  const updatedPublishingStatuses: Record<SocialMediaAccountId, MediaTypeLoadingStates> = {};

  state.accountIdsToPublishTo.forEach((accountId) => {
    updatedPublishingStatuses[accountId] = getSettledPublishingStatusesForAccount(state, accountId);
  });

  state.publishingStatusForMediaTypes = updatedPublishingStatuses;
};

const doSyncPublishingParams = (state: SocialMediaWizardState): void => {
  const accountsToPublishTo = state.accountIdsToPublishTo;
  const currentPublishingParams = state.publishingParams;
  const updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams> = {};

  accountsToPublishTo.forEach((accountId) => {
    if (typeof currentPublishingParams[accountId] === 'undefined') {
      setEmptyStatePublishingParamsIfNeeded(state, accountId);
      initGraphicsMeta(state);
    }

    const paramsForAccount = state.publishingParams[accountId];

    if (paramsForAccount) {
      updatedPublishingParams[accountId] = {
        [PlatformMediaType.STORIES]: paramsForAccount.STORIES,
        [PlatformMediaType.POST]: paramsForAccount.POST,
        [PlatformMediaType.REELS]: paramsForAccount.REELS,
      };
    }
  });

  state.publishingParams = updatedPublishingParams;

  syncCommonCaptionWithPublishingParams(state);
};

const syncCommonCaptionWithPublishingParams = (state: SocialMediaWizardState): void => {
  const nonStoryCaptions = getNonStoryCaptionsForAccountsToPublishTo(state);

  if (nonStoryCaptions.length === 0) {
    return;
  }

  const [firstCaption, ...remainingCaptions] = nonStoryCaptions;

  if (!remainingCaptions.every((caption) => caption === firstCaption)) {
    state.hasCommonCaption = false;
    return;
  }

  if (state.hasCommonCaption) {
    state.commonCaption = firstCaption;
  }
};

const getNonStoryCaptionsForAccountsToPublishTo = (state: SocialMediaWizardState): string[] => {
  const captions: string[] = [];

  state.accountIdsToPublishTo.forEach((accountId) => {
    const mediaTypeParams = state.publishingParams[accountId];

    if (!mediaTypeParams) {
      return;
    }

    const nonStoryParams = getNonStoryPublishingParams(mediaTypeParams);

    if (nonStoryParams) {
      captions.push(nonStoryParams.caption);
    }
  });

  return captions;
};

const doUpdatePublishingStatusForAccountWithMediaType = (state: SocialMediaWizardState, payload: PublishingStatusForAccountPayload): void => {
  const mediaType = payload.platformMediaType;
  const accountId = payload.idSocialMediaAccount;

  const {publishingStatusForMediaTypes} = state;
  publishingStatusForMediaTypes[accountId][mediaType] = payload.publishingStatus;

  // A flow-set LOADED is the completion evidence that survives re-inits; init-derived LOADED ("no params") never reaches here.
  if (payload.publishingStatus === LoadingStates.LOADED) {
    state.publishCompletedForMediaTypes[accountId] ??= {};
    state.publishCompletedForMediaTypes[accountId][mediaType] = true;
  }
};

/**
 * Re-arms the params-save signal for a fresh publish/save/schedule cycle. savingPublishingParamsState survives
 * a completed cycle as LOADED, and a PublishingPost mounting into that stale LOADED fires doPublishForAccount
 * BEFORE the new cycle's updateSocialMediaPostPublishingParams save has run — the premature job runs against a
 * post whose new slot params are not in the DB yet and burns the slot's one-shot publish claim, so the
 * legitimate post-save job loses the claim and the post wedges in PUBLISHING (PR #3428). The two callers guard
 * the cycle's two independently-triggered halves: the publishing-action dispatch closes the gate before the
 * publish screen can mount (PublishingPost renders off publishingAction, step-independent), and the
 * wizard-step edge into PUBLISH closes it before the save cycle the [step] effect drives — browser-history
 * re-entry writes the step directly and never dispatches the action.
 */
const doArmParamsSaveForNewPublishCycle = (state: SocialMediaWizardState): void => {
  doUpdateSavingPublishingParamsState(state, LoadingStates.NOT_LOADED);
};

const doUpdatePublishingAction = (state: SocialMediaWizardState, payload: SocialPostPublishingAction | undefined): void => {
  state.publishingAction = payload;
  doArmParamsSaveForNewPublishCycle(state);
};

const isCountValid = (caption: string, account: SocialAccount): boolean => {
  const characterLimit = getCharacterLimitForAccountPost(account);

  return caption.length <= characterLimit;
};

const isTwitterHashtagCountValid = (caption: string, accountType: SocialPlatform): boolean => {
  return accountType === SocialPlatform.TWITTER ? countHashtags(caption) <= 30 : true;
};

const isCaptionValid = (caption: string, account: SocialAccount): boolean => {
  switch (account.type) {
    case SocialPlatform.TWITTER:
      return isCountValid(caption, account) && isTwitterHashtagCountValid(caption, account.type);
    default:
      return isCountValid(caption, account);
  }
};

const getTitleErrorIfTitleIsInvalid = (title: string, accountType: SocialPlatform, areUrlsAllowed: boolean): string | null => {
  if (!areUrlsAllowed && isValidURL(title)) {
    return window.i18next.t('pmwjs_title_url_error');
  }

  const currentTitleValidation = titleValidations[accountType];

  if (currentTitleValidation.limit && title.length > currentTitleValidation.limit) {
    return window.i18next.t('pmwjs_caption_long_error', {limit: currentTitleValidation.limit});
  }

  return null;
};

const doResetTitleErrorForAccount = (account: SocialAccount, state: SocialMediaWizardState): void => {
  if (state.titleError?.[account.id] !== undefined) {
    state.titleError[account.id] = null;
  }
};

const doSetTitleErrorForAccount = (account: SocialAccount, state: SocialMediaWizardState, titleError: string): void => {
  if (state.titleError?.[account.id] !== undefined) {
    state.titleError[account.id] = titleError;
  }
};

const doResetCaptionErrorForAccount = (account: SocialAccount, state: SocialMediaWizardState): void => {
  if (state.captionError?.[account.id] !== undefined) {
    state.captionError[account.id] = CaptionErrorTypes.NO_ERROR;
  }
};

const doSetCaptionCountErrorForAccount = (account: SocialAccount, state: SocialMediaWizardState): void => {
  if (state.captionError?.[account.id] !== undefined) {
    state.captionError[account.id] = CaptionErrorTypes.COUNT_ERROR;
  }
};

const doSetCaptionHashtagErrorForAccount = (account: SocialAccount, state: SocialMediaWizardState): void => {
  if (state.captionError?.[account.id] !== undefined) {
    state.captionError[account.id] = CaptionErrorTypes.HASHTAG_ERROR;
  }
};

const doUpdatePublishingParamCaption = (state: SocialMediaWizardState, payload: PublishingParamsCaptionPayload): void => {
  const {caption} = payload;
  const account = getAccountForId(state.accounts, payload.idSocialMediaAccount);

  if (!account) {
    return;
  }

  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('caption' in publishingParams) {
        publishingParams.caption = payload.caption;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );

  if (isCaptionValid(caption, account)) {
    doResetCaptionErrorForAccount(account, state);
  } else {
    if (!isCountValid(caption, account)) {
      doSetCaptionCountErrorForAccount(account, state);
      return;
    }

    doSetCaptionHashtagErrorForAccount(account, state);
  }
};

const doUpdatePublishingParamTitle = (state: SocialMediaWizardState, payload: PublishingParamsTitlePayload): void => {
  const account = getAccountForId(state.accounts, payload.idSocialMediaAccount);

  if (!account) {
    return;
  }

  const accountType = account.type;
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('title' in publishingParams) {
        publishingParams.title = payload.title;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );

  const titleError = getTitleErrorIfTitleIsInvalid(payload.title, accountType, payload.areURLsAllowed);

  if (titleError) {
    doSetTitleErrorForAccount(account, state, titleError);
  } else {
    doResetTitleErrorForAccount(account, state);
  }
};

const doUpdatePublishingParamText = (state: SocialMediaWizardState, payload: PublishingParamsTextUpdatePayload): void => {
  const account = getAccountForId(state.accounts, payload.idSocialMediaAccount);
  if (!account) {
    return;
  }

  const publishingParamsForAccount = state.publishingParams[payload.idSocialMediaAccount];
  if (!publishingParamsForAccount) {
    return;
  }

  const accountType = account.type;
  const platformMediaType = getNonStoryPlatformMediaType(publishingParamsForAccount);
  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('title' in publishingParams) {
        if (shouldUpdateTitle(account, publishingParams)) {
          publishingParams.title = payload.title;
        }
      }

      if ('caption' in publishingParams) {
        publishingParams.caption = payload.caption;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );

  const titleError = getTitleErrorIfTitleIsInvalid(payload.title, accountType, payload.areURLsAllowed);

  if (titleError) {
    doSetTitleErrorForAccount(account, state, titleError);
  } else {
    doResetTitleErrorForAccount(account, state);
  }
};

const doUpdatePublishingParamDisableComment = (state: SocialMediaWizardState, payload: PublishingParamsDisableComment): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('disableComment' in publishingParams) {
        publishingParams.disableComment = payload.disableComment;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdatePublishingParamDisableStitch = (state: SocialMediaWizardState, payload: PublishingParamsDisableStitch): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('disableStitch' in publishingParams) {
        publishingParams.disableStitch = payload.disableStitch;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

export const doUpdateAreAccountsLoading = (state: SocialMediaWizardState, payload: boolean): void => {
  state.areAccountsLoading = payload;
};

const doUpdatePublishingParamDisableDuet = (state: SocialMediaWizardState, payload: PublishingParamsDisableDuet): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('disableDuet' in publishingParams) {
        publishingParams.disableDuet = payload.disableDuet;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdatePublishingParamIsPromotingThirdParty = (state: SocialMediaWizardState, payload: PublishingParamsIsPromotingThirdParty): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('isPromotingThirdParty' in publishingParams) {
        publishingParams.isPromotingThirdParty = payload.isPromotingThirdParty;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdatePublishingParamIsSelfBrand = (state: SocialMediaWizardState, payload: PublishingParamsIsSelfBrand): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('isSelfBrand' in publishingParams) {
        publishingParams.isSelfBrand = payload.isSelfBrand;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdateLinkedInPublishingParamPrivacySettings = (state: SocialMediaWizardState, payload: PublishingParamsLinkedInPrivacySettingsPayload): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('privacySettings' in publishingParams) {
        publishingParams.privacySettings = payload.privacySettings;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdatePublishErrorMessageForAllParams = (state: SocialMediaWizardState, payload: string): void => {
  for (const accountId of state.accountIdsToPublishTo) {
    for (const platformMediaType of Object.values(PlatformMediaType)) {
      const updatePayload: PublishingParamsPublishErrorMessagePayload = {
        publishErrorMessage: payload,
        idSocialMediaAccount: accountId,
        platformMediaType,
      };

      doUpdatePublishErrorMessageForAccount(state, updatePayload);
    }
  }
};

const doUpdatePublishErrorMessageForAccount = (state: SocialMediaWizardState, payload: PublishingParamsPublishErrorMessagePayload): void => {
  const {platformMediaType} = payload;

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('publishErrorMessage' in publishingParams) {
        publishingParams.publishErrorMessage = payload.publishErrorMessage;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdateInstagramReelMusicForAccount = (state: SocialMediaWizardState, payload: PublishingParamsReelMusicPayload): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('reelMusic' in publishingParams) {
        publishingParams.reelMusic = payload.reelMusic;
      }
    },
    platformMediaType
  );
};

const doUpdatePinterestBoardIdForAccount = (state: SocialMediaWizardState, payload: PublishingParamsBoardIdPayload): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('boardId' in publishingParams) {
        publishingParams.boardId = payload.boardId;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdatePinterestWebsiteLinkForAccount = (state: SocialMediaWizardState, payload: PublishingParamsWebsiteLinkPayload): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('websiteLink' in publishingParams) {
        publishingParams.websiteLink = payload.websiteLink;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdateYouTubePublishingParamPrivacySettings = (state: SocialMediaWizardState, payload: PublishingParamsYouTubePrivacySettingsPayload): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('privacySettings' in publishingParams) {
        publishingParams.privacySettings = payload.privacySettings;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdateTikTokPublishingParamPrivacySettings = (state: SocialMediaWizardState, payload: PublishingParamsTikTokPrivacySettingsPayload): void => {
  const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[payload.idSocialMediaAccount]);

  if (!platformMediaType) {
    return;
  }

  doUpdatePublishingParamsProperty(
    state,
    payload.idSocialMediaAccount,
    (publishingParams) => {
      if ('privacySettings' in publishingParams) {
        publishingParams.privacySettings = payload.privacySettings;
      }

      state.publishingParams[payload.idSocialMediaAccount][platformMediaType] = publishingParams;
    },
    platformMediaType
  );
};

const doUpdatePublishingParamAllCaptions = (state: SocialMediaWizardState, commonCaptionPayload: PublishingParamsCommonCaptionPayload): void => {
  const {caption} = commonCaptionPayload;
  const {isMultiCharacterChange} = commonCaptionPayload;

  state.accountIdsToPublishTo.forEach((accountId) => {
    doUpdatePublishingParamCaption(state, {caption, idSocialMediaAccount: accountId, isMultiCharacterChange});
  });
};

const doUpdatePublishingParamAllText = (state: SocialMediaWizardState, commonCaptionPayload: PublishingParamsCommonTextPayload): void => {
  state.accountIdsToPublishTo.forEach((accountId) => {
    const payloadData = commonCaptionPayload[accountId];

    if (!payloadData) {
      return;
    }

    const caption = payloadData.caption;
    const title = payloadData.title;
    const areURLsAllowed = payloadData.areURLsAllowed ?? true;
    doUpdatePublishingParamText(state, {caption, title, areURLsAllowed, idSocialMediaAccount: accountId});
  });
};

const doUpdatePublishingParamSocialPostCover = (state: SocialMediaWizardState, socialPostCoverPayload: PublishingParamsSocialPostCoverPayload): void => {
  const {platformMediaType, idAccount, design, coverObject} = socialPostCoverPayload;

  if (state.hasCommonPostCover) {
    doUpdatePublishingParamAllSocialPostCovers(state, {design, coverObject});
  } else {
    setPostCoverInPublishParamsIfNeeded(coverObject, state.publishingParams[idAccount][platformMediaType], design);
  }
};

const doUpdatePublishingParamAllSocialPostCovers = (state: SocialMediaWizardState, commonPostCoverPayload: PublishingParamsCommonPostCoverPayload): void => {
  const {design, coverObject} = commonPostCoverPayload;

  const accountsToAddCoverTo = getAccountsToAddCommonCoverTo(state);

  accountsToAddCoverTo.forEach((account) => {
    const platformMediaType = getNonStoryPlatformMediaType(state.publishingParams[account.id]);
    if (!platformMediaType) {
      return;
    }

    if (isCoverGeneratedThroughUpload(coverObject) && !socialAccountPostCoverValidations[account.type].isUploadAllowed) {
      return;
    }

    setPostCoverInPublishParamsIfNeeded(coverObject, state.publishingParams[account.id][platformMediaType], design);
  });
};

const doResetSocialPostCoverForAllPublishingParams = (state: SocialMediaWizardState): void => {
  state.accountIdsToPublishTo.forEach((accountId) => {
    const mediaTypePublishingParams = state.publishingParams[accountId];
    if (!mediaTypePublishingParams) {
      return;
    }

    const platformMediaType = getNonStoryPlatformMediaType(mediaTypePublishingParams);
    if (!platformMediaType) {
      return;
    }

    const currentPublishingParams = state.publishingParams[accountId][platformMediaType];
    if (!currentPublishingParams) {
      return;
    }

    const isSingleGraphic = isSingleGraphicMetaMap(currentPublishingParams.graphicsMetaMap);

    if (!isSingleGraphic) {
      return;
    }

    const posterHashedId = Object.keys(currentPublishingParams.graphicsMetaMap)[0];
    const pageHashedId = Object.keys(currentPublishingParams.graphicsMetaMap[posterHashedId])[0];
    const graphicsMeta = currentPublishingParams.graphicsMetaMap[posterHashedId][pageHashedId];

    currentPublishingParams.graphicsMetaMap[posterHashedId][pageHashedId] = {
      ...graphicsMeta,
      cover: undefined,
    };
  });
};

const doUpdatePost = (state: SocialMediaWizardState, payload: SocialPost): void => {
  state.post = payload;
};

const doUpdateCommonCaption = (state: SocialMediaWizardState, payload: string): void => {
  state.commonCaption = payload;
};

const doUpdateHasCommonCaption = (state: SocialMediaWizardState, payload: boolean): void => {
  state.hasCommonCaption = payload;
};

const doUpdateEmphasizedPlatform = (state: SocialMediaWizardState, payload: SocialPlatform | undefined): void => {
  state.emphasizedPlatform = payload;
};

const doUpdateHasCommonPostCover = (state: SocialMediaWizardState, payload: boolean): void => {
  state.hasCommonPostCover = payload;
};

const doUpdatePublishingParams = (state: SocialMediaWizardState, payload: Record<SocialMediaAccountIdString, MediaTypePublishingParams>): void => {
  state.publishingParams = payload;
  doDropPlanLockedAccountsFromSelection(state);
};

const doUpdateWizardStep = (state: SocialMediaWizardState, payload: SocialMediaWizardStep): void => {
  // Re-arm only on the edge INTO the publish step: a same-value write does not re-run the [step] effect that
  // re-drives the save to LOADED, so re-arming there would wipe the fan-out signal mid-cycle.
  const isEnteringPublishStep = payload === SocialMediaWizardStep.PUBLISH && state.wizardStep !== SocialMediaWizardStep.PUBLISH;
  state.wizardStep = payload;

  if (isEnteringPublishStep) {
    doArmParamsSaveForNewPublishCycle(state);
  }
};

const doUpdateNextButtonIcon = (state: SocialMediaWizardState, payload: string): void => {
  state.nextButtonIcon = payload;
};

const doUpdateNextButtonText = (state: SocialMediaWizardState, payload: string): void => {
  state.nextButtonText = payload;
};

const doUpdateNumParentAccountsConnected = (state: SocialMediaWizardState, payload: number): void => {
  state.numParentAccountsConnected = payload;
};

const doHideNextButton = (state: SocialMediaWizardState): void => {
  state.hideNextButton = true;
};

const doShowNextButton = (state: SocialMediaWizardState): void => {
  state.hideNextButton = false;
};

const doEnableNextButton = (state: SocialMediaWizardState): void => {
  state.nextButtonDisabled = false;
};

const doDisableNextButton = (state: SocialMediaWizardState): void => {
  state.nextButtonDisabled = true;
};

const doUpdateTriggerNext = (state: SocialMediaWizardState): void => {
  state.triggerNext += 1;
};

const doUpdateTriggerPrevious = (state: SocialMediaWizardState): void => {
  state.triggerPrevious += 1;
};

export const updateWizardSocialAccounts = (state: SocialMediaWizardState, payload: SocialAccount[]): void => {
  state.accounts = payload;
};

const doUpdateWizardSocialAccount = (state: SocialMediaWizardState, payload: SocialAccount): void => {
  const {accounts} = state;
  const updatedAccounts: SocialAccount[] = [];

  accounts.forEach((account) => {
    if (payload.id === account.id) {
      updatedAccounts.push(payload);
    } else {
      updatedAccounts.push(account);
    }
  });

  state.accounts = updatedAccounts;
};

const doRemoveWizardSocialAccount = (state: SocialMediaWizardState, payload: number): void => {
  state.accounts = state.accounts.filter((account) => {
    return account.id !== payload;
  });

  state.accountIdsToPublishTo = state.accountIdsToPublishTo.filter((accountId) => {
    return accountId !== payload;
  });
};

const doUpdateIsTemplateSelected = (state: SocialMediaWizardState, payload: boolean): void => {
  state.isTemplateSelected = payload;
};

const doUpdateAccountLevelErrorMessage = (state: SocialMediaWizardState, payload: ErrorBoxInputs): void => {
  state.accountLevelErrorMessage = payload;
};

const doAccountsShareOnePlatform = (accounts: SocialAccount[]): boolean => {
  const platforms = new Set(accounts.map((account) => getComparableSocialPlatform(account.type)));

  return platforms.size <= 1;
};

const doApplyCommonCaptionDefault = (state: SocialMediaWizardState, accounts: SocialAccount[]): void => {
  const shareOnePlatform = doAccountsShareOnePlatform(accounts);

  if (state.accountIdsToPublishTo.length === 0 || !shareOnePlatform) {
    state.hasCommonCaption = shareOnePlatform;
  }
};

const getPlanLockedAccountIds = (state: SocialMediaWizardState): number[] => {
  return state.accounts.filter(isAccountPlanLocked).map((account) => account.id);
};

const doUpdateAccountIdsToPublishTo = (state: SocialMediaWizardState, payload: number[]): void => {
  const planLockedAccountIds = getPlanLockedAccountIds(state);
  const unlockedAccountIds = payload.filter((accountId) => !planLockedAccountIds.includes(accountId));
  const accounts = state.accounts.filter((account) => {
    return unlockedAccountIds.includes(account.id);
  });

  if (state.post) {
    state.post.accounts = accounts;
  }

  doApplyCommonCaptionDefault(state, accounts);

  state.accountIdsToPublishTo = [...unlockedAccountIds].sort((firstAccountId, secondAccountId) => {
    return firstAccountId - secondAccountId;
  });
};

/**
 * Drops ONLY the completion evidence for a deselected account, so if it is re-selected an evidence-less
 * LOADED is no longer preserved on re-init and its publish kickoff fires again. The status / url / retry maps
 * are intentionally left keyed: their write sites index by account id without a guard, so a late in-flight
 * publish response or a popstate re-entry for this account would throw if the entry were removed.
 */
const doRemoveAccountIdFromPublishing = (state: SocialMediaWizardState, payload: number): void => {
  state.accountIdsToPublishTo = state.accountIdsToPublishTo.filter((accountId) => {
    return accountId !== payload;
  });

  state.publishCompletedForMediaTypes = omitKey(state.publishCompletedForMediaTypes, String(payload));
  state.retainedNonStoryTextForAccount = omitKey(state.retainedNonStoryTextForAccount, String(payload));
};

const doRemoveAccountsFromWizard = (state: SocialMediaWizardState, deletedAccountIds: number[]): void => {
  const deletedAccounts = state.accounts.filter((account) => {
    return deletedAccountIds.includes(account.id);
  });

  state.accounts = state.accounts.filter((account) => {
    return !deletedAccountIds.includes(account.id);
  });
  state.numParentAccountsConnected = Math.max(0, state.numParentAccountsConnected - getNumParentAccounts(deletedAccounts));

  for (const deletedAccountId of deletedAccountIds) {
    doRemoveAccountIdFromPublishing(state, deletedAccountId);
  }
};

const doRemoveAccountFromPublishingParams = (state: SocialMediaWizardState, payload: number): void => {
  state.publishingParams = Object.fromEntries(Object.entries(state.publishingParams).filter(([accountId]) => Number(accountId) !== payload));
};

export const doDropPlanLockedAccountsFromSelection = (state: SocialMediaWizardState): void => {
  const planLockedAccountIds = getPlanLockedAccountIds(state);
  if (planLockedAccountIds.length === 0) {
    return;
  }

  doUpdateAccountIdsToPublishTo(state, state.accountIdsToPublishTo);

  planLockedAccountIds.forEach((lockedAccountId) => {
    doRemoveAccountFromPublishingParams(state, lockedAccountId);
  });
};

const doAddAccountIdForPublishing = (state: SocialMediaWizardState, payload: number): void => {
  if (state.accountIdsToPublishTo.includes(payload)) {
    return;
  }
  state.accountIdsToPublishTo.push(payload);
};

const doUpdateAccountTypeToConnect = (state: SocialMediaWizardState, payload: SocialPlatform): void => {
  state.accountTypeToConnect = payload;
};

const doSetAccountResizeStatusForMediaType = (state: SocialMediaWizardState, payload: AccountResizeStatusForDesignPayload): void => {
  const {idPoster} = payload;
  const {idSocialMediaAccount} = payload;

  if (payload.postResizeStatus) {
    state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.POST] = {
      ...state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.POST],
      [idPoster]: {
        ...state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.POST][idPoster],
        [payload.idPage]: payload.postResizeStatus,
      },
    };
  }

  if (payload.reelResizeStatus) {
    state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.REELS] = {
      ...state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.REELS],
      [idPoster]: {
        ...state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.REELS][idPoster],
        [payload.idPage]: payload.reelResizeStatus,
      },
    };
  }

  if (payload.storyResizeStatus) {
    state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.STORIES] = {
      ...state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.STORIES],
      [idPoster]: {
        ...state.accountResizeStatusMapForMediaType[idSocialMediaAccount][PlatformMediaType.STORIES][idPoster],
        [payload.idPage]: payload.storyResizeStatus,
      },
    };
  }
};

const doResetAccountResizeStatusForMediaType = (state: SocialMediaWizardState): void => {
  const connectedAccounts = state.accounts;
  const initialMap: AccountResizeStatusMapForMediaType = {};

  for (const account of connectedAccounts) {
    const accountId = account.id;

    const designs = state.designsToPublish;
    const mapForDesigns: Record<PlatformMediaType, AccountResizeStatusMap> = {
      [PlatformMediaType.POST]: {},
      [PlatformMediaType.STORIES]: {},
      [PlatformMediaType.REELS]: {},
    };

    for (const design of designs) {
      mapForDesigns[PlatformMediaType.POST] = {
        ...mapForDesigns[PlatformMediaType.POST],
        [design.posterHashedId]: {
          ...mapForDesigns[PlatformMediaType.POST][design.posterHashedId],
          [design.pageHashedId]: AccountResizeStatus.NOT_RESIZED,
        },
      };

      mapForDesigns[PlatformMediaType.STORIES] = {
        ...mapForDesigns[PlatformMediaType.STORIES],
        [design.posterHashedId]: {
          ...mapForDesigns[PlatformMediaType.STORIES][design.posterHashedId],
          [design.pageHashedId]: AccountResizeStatus.NOT_RESIZED,
        },
      };

      mapForDesigns[PlatformMediaType.REELS] = {
        ...mapForDesigns[PlatformMediaType.REELS],
        [design.posterHashedId]: {
          ...mapForDesigns[PlatformMediaType.REELS][design.posterHashedId],
          [design.pageHashedId]: AccountResizeStatus.NOT_RESIZED,
        },
      };
    }
    initialMap[accountId] = mapForDesigns;
  }

  state.accountResizeStatusMapForMediaType = initialMap;
};

const doUpdatePublishedAccountUrlForMediaType = (state: SocialMediaWizardState, payload: PublishedUrlUpdatePayload): void => {
  const idSocialMediaAccount = payload.accountId;
  const platformMediaType = payload.mediaType;
  state.publishedAccountUrlMapForMediaType[idSocialMediaAccount][platformMediaType] = payload.url;
};

const setEmptyStatePublishingParamsIfNeeded = (state: SocialMediaWizardState, idSocialMediaAccount: SocialMediaAccountId): void => {
  if (typeof state.publishingParams[idSocialMediaAccount] === 'undefined') {
    const matchedAccount: SocialAccount | null = getAccountForId(state.accounts, idSocialMediaAccount);

    if (matchedAccount) {
      const publishingParams: SocialMediaPublishingParams | null = getEmptyPublishingParamsForAccount(matchedAccount);

      if (publishingParams) {
        const {posterHashedId, pageHashedId} = state.designsToPublish[0];
        // The graphics meta loads async; entering the details step before it lands must not throw.
        const isInstagramReel = state.designsToPublish.length === 1 && (state.designsGraphicsMetaMap[posterHashedId]?.[pageHashedId]?.isVideo ?? false);
        publishingParams.caption = getCaptionForRebuiltNonStoryParams(state, idSocialMediaAccount);
        state.publishingParams[idSocialMediaAccount] = getDefaultPublishParamsForMediaType(matchedAccount, publishingParams, isInstagramReel);
      }
    }
  }
};

const applyPolledPreviewUrls = (state: SocialMediaWizardState): void => {
  for (const {posterHashedId, pageHashedId, previewUrl} of state.polledPreviewUrls) {
    const graphicMeta = state.designsGraphicsMetaMap[posterHashedId]?.[pageHashedId];
    if (graphicMeta) {
      graphicMeta.graphicURL = previewUrl;
    }
  }
};

const initGraphicsMeta = (state: SocialMediaWizardState): void => {
  if (typeof state.publishingParams === 'undefined') {
    return;
  }

  const userAccounts = state.accountIdsToPublishTo;

  for (const accountId of userAccounts) {
    if (typeof state.publishingParams[accountId] === 'undefined') {
      return;
    }

    const postPublishingParams = getNonStoryPublishingParams(state.publishingParams[accountId]);
    const storyPublishingParams = getStoryPublishingParams(state.publishingParams[accountId]);

    if (postPublishingParams) {
      const postCover = getPosterCoverCopyFromPublishParams(state, postPublishingParams);
      postPublishingParams.graphicsMetaMap = cloneDeep(state.designsGraphicsMetaMap);
      setPostCoverInPublishParamsIfNeeded(postCover, postPublishingParams, state.designsToPublish[0]);
    }

    if (storyPublishingParams) {
      storyPublishingParams.graphicsMetaMap = state.designsGraphicsMetaMap;
    }
  }
};

const initStoryGuidelines = (state: SocialMediaWizardState): void => {
  const storyGuidelineMap: Record<SocialMediaAccountId, StoryGuideline> = {};
  const randomNumbersTaken: number[] = [];

  for (const accountId of state.accountIdsToPublishTo) {
    const account = state.accounts.find((acc) => acc.id === accountId);
    if (!account) {
      return;
    }
    const newNumber = getUnusedRandomGuidelineNumber(randomNumbersTaken, account.type);
    storyGuidelineMap[accountId] = getStoryGuidelineforIndex(newNumber, account.type);
    randomNumbersTaken.push(newNumber);
  }

  state.storyGuidelineForAccount = storyGuidelineMap;
};

const getAccountsToAddCommonCoverTo = (state: SocialMediaWizardState): SocialAccount[] => {
  return state.accounts.filter((account) => {
    return socialAccountPostCoverValidations[account.type].doesSupportCover && state.accountIdsToPublishTo.includes(account.id);
  });
};

const getSocialPostCoverFromPublishingParams = (publishingParamsGraphicsMetaMap: GraphicsMetaMapType): SocialPostCover | undefined => {
  const pageGraphicsMetaMaps = Object.values(publishingParamsGraphicsMetaMap);
  if (pageGraphicsMetaMaps.length > 0) {
    return Object.values(pageGraphicsMetaMaps[0])[0]?.cover;
  }

  return undefined;
};

const getPosterCoverCopyFromPublishParams = (state: SocialMediaWizardState, postPublishingParams: SocialMediaPublishingParams | null): SocialPostCover | undefined => {
  let postCover;

  if (isSingleGraphicPost(state) && postPublishingParams?.graphicsMetaMap) {
    postCover = getSocialPostCoverFromPublishingParams(postPublishingParams.graphicsMetaMap);
  }

  return postCover;
};

const setPostCoverInPublishParamsIfNeeded = (postCover: SocialPostCover | undefined, postPublishingParams: SocialMediaPublishingParams | null, design: DesignSelection): void => {
  if (postCover && postPublishingParams) {
    postPublishingParams.graphicsMetaMap[design.posterHashedId][design.pageHashedId].cover = postCover;
  }
};

const isSingleGraphicPost = (state: SocialMediaWizardState): boolean => {
  return state.designsToPublish.length === 1;
};

const isSingleGraphicMetaMap = (graphicMetaMap: GraphicsMetaMapType): boolean => {
  return Object.values(graphicMetaMap).length === 1 && Object.values(Object.values(graphicMetaMap)[0]).length === 1;
};

const isCoverGeneratedThroughUpload = (coverObject: SocialPostCover): boolean => {
  return coverObject.timestamp === null;
};

const initTitleErrors = (state: SocialMediaWizardState): void => {
  const numberAccounts = state.accountIdsToPublishTo.length;
  const accountsToPublish = state.accountIdsToPublishTo;
  const titleErrorsNew: TitleErrorByAccountIds = {};

  for (let i = 0; i < numberAccounts; i++) {
    if (typeof state.titleError[accountsToPublish[i]] === 'undefined') {
      const validPublishingParams = getNonStoryPublishingParams(state.publishingParams[accountsToPublish[i]]);

      if (validPublishingParams && 'title' in validPublishingParams && validPublishingParams.title !== 'undefined') {
        const {title} = validPublishingParams;
        const account = getAccountForId(state.accounts, accountsToPublish[i]);

        if (!account) {
          return;
        }

        const titleError = getTitleErrorIfTitleIsInvalid(title, account.type, titleValidations[account.type].areURLsAllowed);

        if (titleError) {
          titleErrorsNew[accountsToPublish[i]] = titleError;
        } else {
          titleErrorsNew[accountsToPublish[i]] = null;
        }
      }
    } else {
      titleErrorsNew[accountsToPublish[i]] = state.titleError[accountsToPublish[i]];
    }
  }

  state.titleError = titleErrorsNew;
};

const initCaptionErrors = (state: SocialMediaWizardState): void => {
  const numberAccounts = state.accountIdsToPublishTo.length;
  const accountsToPublish = state.accountIdsToPublishTo;
  const captionErrorsNew: CaptionErrorByAccountIds = {};

  for (let i = 0; i < numberAccounts; i++) {
    if (typeof state.captionError[accountsToPublish[i]] === 'undefined') {
      const validPublishingParams = getNonStoryPublishingParams(state.publishingParams[accountsToPublish[i]]);

      if (validPublishingParams && validPublishingParams.caption !== 'undefined') {
        const existingCaption = validPublishingParams.caption;
        const account = getAccountForId(state.accounts, accountsToPublish[i]);

        if (!account) {
          return;
        }

        if (isCaptionValid(existingCaption, account)) {
          captionErrorsNew[accountsToPublish[i]] = CaptionErrorTypes.NO_ERROR;
        }

        if (!isCountValid(existingCaption, account)) {
          captionErrorsNew[accountsToPublish[i]] = CaptionErrorTypes.COUNT_ERROR;
        }

        if (!isTwitterHashtagCountValid(existingCaption, account.type)) {
          captionErrorsNew[accountsToPublish[i]] = CaptionErrorTypes.HASHTAG_ERROR;
        }
      }
    } else {
      captionErrorsNew[accountsToPublish[i]] = state.captionError[accountsToPublish[i]];
    }
  }

  state.captionError = captionErrorsNew;
};

const shouldUpdateTitle = (account: SocialAccount, publishingParams: SocialMediaPublishingParams): boolean => {
  let doParamsHaveVideo = false;
  let doParamsHaveImage = false;

  const pageGraphicMetaMaps = Object.values(publishingParams.graphicsMetaMap);
  const graphicMetas = pageGraphicMetaMaps.flatMap((pageMeta) => Object.values(pageMeta));

  for (const graphicsMeta of graphicMetas) {
    if (graphicsMeta.isVideo) {
      doParamsHaveVideo = true;
    } else {
      doParamsHaveImage = true;
    }
  }

  return doesSocialPlatformHaveTitle(account.type, doParamsHaveVideo, doParamsHaveImage);
};

export const {
  updateAccountTypeToConnect,
  removeAccountIdFromPublishing,
  removeAccountsFromWizard,
  removeAccountFromPublishingParams,
  updateTriggerNext,
  updateTriggerPrevious,
  updateIsTemplateSelected,
  updateAccountLevelErrorMessage,
  hideAccountLevelErrorMessage,
  updateWizardStep,
  disableNextButton,
  enableNextButton,
  addAccountIdForPublishing,
  updateAccountIdsToPublishTo,
  updatePublishingParamCaption,
  updatePublishingParamAllCaptions,
  updatePublishingParamAllText,
  updatePublishingParamSocialPostCover,
  updatePublishingParamTitle,
  updatePublishingParams,
  updatePublishingAction,
  updateSavingPublishingParamsState,
  updateWizardSocialAccount,
  removeWizardSocialAccount,
  updatePublishingParamDisableComment,
  updatePublishingParamDisableDuet,
  updatePublishingParamIsSelfBrand,
  updatePublishingParamDisableStitch,
  updatePublishingParamIsPromotingThirdParty,
  resetSocialPostCoverForAllPublishingParams,
  updateTikTokPublishingParamPrivacySettings,
  updateLinkedInPublishingParamPrivacySettings,
  updateYouTubePublishingParamPrivacySettings,
  updatePinterestPublishingParamsWebsiteLink,
  updatePinterestPublishingParamsBoardId,
  updateInstagramReelMusic,
  updateNumParentAccountsConnected,
  initPublishingParams,
  syncPublishingParams,
  updatePost,
  updateNextButtonText,
  updateNextButtonIcon,
  updateHasCommonCaption,
  updateEmphasizedPlatform,
  updateCommonCaption,
  updateHasSeenOnboarding,
  updateHasSeenConfetti,
  resetStateToDraft,
  updatePublishErrorMessageForAccount,
  updateHasUserPublishedBefore,
  updateIsSocialMediaWizardAiCaptionsEnabled,
  updateIsGoogleBusinessProfileEnabled,
  showNextButton,
  updateDesignsToPublish,
  updateDesignsToPublishAndTriggerNext,
  updateAccountNeedsResizeMap,
  updateMultiGraphicDesignSelectorMode,
  resetAccountResizeStatusForDesignsMap,
  syncEditedDesign,
  updateDesignSelectorTab,
  updateGraphicURLInGraphicsMetaForDesign,
  setAccountResizeStatusForMediaType,
  updatePublishableUrl,
  setPublishToReel,
  setPublishToPost,
  setPublishToStory,
  updatePublishingStatusForAllAccountMediaTypes,
  resetIncompletePublishingStatuses,
  updatePublishingStatusForAccountWithMediaType,
  updatePublishedAccountPostUrlMapForAccountWithMediaType,
  initRetryPublishingMapForAccount,
  unsetRetryingStatusForAccountWithMediaType,
  initPublishingStatusMapForAccounts,
  initPublishedUrlMapForAccounts,
  toggleToPostOnly,
  toggleToStoryOnly,
  setMediaTypeSelectionToPost,
  setMediaTypeSelectionToStory,
  setMediaTypeSelectionToReelAndStory,
  setMediaTypeSelectionToPostAndStory,
  setMediaTypeSelectionToReel,
  toggleToReelOnly,
  updateDesignToResize,
  updateHasCommonPostCover,
  updatePublishErrorMessageForAllParams,
  setRetryingStatusForAccountWithMediaType,
  updateDesignsGraphicMetaMap,
  updateDesignsGraphicURLs,
  clearPolledPreviewUrls,
  setAreCaptionsBeingAutoGenerated,
  updateGraphicsReadiness,
} = socialMediaWizardSlice.actions;

export const socialMediaWizardReducer = socialMediaWizardSlice.reducer;
